import { isEmpty } from '@ember/utils';
// Types ship with the self-hosted bundle; see tinymce-loader.
import type {
  Editor,
  Formats,
  RawEditorOptions,
} from '../../public/assets/tinymce/6.8.5/js/tinymce/tinymce';

// TinyMCEConfig is the full editor config, whose plugins, toolbar and menus
// are always strings, so the configs below can append to them.
interface TinyMCEConfig extends RawEditorOptions {
  plugins: string;
  toolbar: string;
  menu: Record<string, { title: string; items: string }>;
  mobile: RawEditorOptions & { plugins: string; toolbar: string };
}

// defaultTinyMCEConfig returns a full WYSIWYG editor for editing content and
// posts.
export function defaultTinyMCEConfig(): TinyMCEConfig {
  return {
    browser_spellcheck: true,
    contextmenu: 'cut copy paste link',
    // https://www.tiny.cloud/docs/tinymce/6/security/#convert-unsafe-embeds
    convert_unsafe_embeds: true,
    elementpath: false,
    inline: false,
    menubar: 'edit insert format table tools',
    min_height: 150,
    paste_data_images: false,
    plugins: 'code link lists table',
    // https://www.tiny.cloud/docs/tinymce/6/editor-premium-upgrade-promotion/
    promotion: false,
    resize: false,
    sandbox_iframes: true,
    statusbar: false,
    theme: 'silver',
    toolbar:
      'undo redo | blocks | bold italic | alignleft aligncenter alignright alignjustify | outdent indent | bullist numlist | link',

    menu: {
      edit: {
        title: 'Edit',
        items: 'undo redo | cut copy paste pastetext | selectall',
      },
      insert: {
        title: 'Insert',
        items: 'link | hr',
      },
      format: {
        title: 'Format',
        items:
          'bold italic underline strikethrough superscript subscript | styles | removeformat',
      },
      table: {
        title: 'Table',
        items: 'inserttable tableprops deletetable | cell row column',
      },
      tools: {
        title: 'Tools',
        items: 'code visualaid',
      },
    },

    mobile: {
      height: '45vh',
      plugins: 'link table fullscreen lists',
      toolbar: 'fullscreen undo redo bold italic link bullist numlist styles',
    },
  };
}

// autosaveTinyMCEConfig returns a full WYSIWYG editor for editing content and
// posts using the default config and enables autosave.
export function autosaveTinyMCEConfig(editorId: string): TinyMCEConfig {
  const defaultConfig = defaultTinyMCEConfig();

  // Enable the Autosave plugin
  defaultConfig.plugins += ' autosave';

  // Autosave plugin settings
  Object.assign(defaultConfig, {
    autosave_interval: '3s',
    autosave_retention: 'P8D',
    // eg: tinymce-autosave-/home/student-notices-ea99c497-3495-4d52-9df8-bbb8376a0c44-
    autosave_prefix: `tinymce-autosave-{path}-${editorId}-`,
  });

  // Add the "Restore last draft" buttons
  defaultConfig.toolbar += ' | restoredraft';
  defaultConfig.mobile.plugins += ' autosave';
  defaultConfig.mobile.toolbar += ' restoredraft';

  return defaultConfig;
}

// minimalTinyMCEConfig returns configuration for basic formatting options.
export function minimalTinyMCEConfig(): RawEditorOptions {
  return {
    // https://www.tiny.cloud/docs/tinymce/6/security/#convert-unsafe-embeds
    convert_unsafe_embeds: true,
    elementpath: false,
    inline: false,
    min_height: 150,
    paste_data_images: false,
    plugins: 'link table',
    // https://www.tiny.cloud/docs/tinymce/6/editor-premium-upgrade-promotion/
    promotion: false,
    resize: true,
    statusbar: true,
    theme: 'silver',

    menu: {
      edit: {
        title: 'Edit',
        items: 'undo redo | cut copy paste pastetext | selectall',
      },
      format: {
        title: 'Format',
        items:
          'bold italic underline strikethrough superscript subscript | styles | removeformat',
      },
      table: {
        title: 'Table',
        items: 'inserttable tableprops deletetable | cell row column',
      },
      tools: {
        title: 'Tools',
        items: 'visualaid',
      },
    },
  };
}

// TemplatedPostEditorStyles provides extra CSS to be added to the editor when
// editing either a Post Template or a post based on one.
const TemplatedPostEditorStyles = `
    span.hero-template-placeholder,
    span.reuse-post-token {
      border: 1px solid #666;
      padding: 0 0.3em 2px;
      border-radius: 1em;
      background-color: #fff;
    }
    span.hero-template-placeholder .material-icons {
      font-size: 20px;
      vertical-align: text-bottom;
    }
    body.mce-content-body:not([contenteditable=true]) {
      background-color: #ddd;
    }
    [data-contenteditable=true],
    body.mce-content-body:not([contenteditable=true]) [contenteditable=true],
    [contenteditable=false] [contenteditable=true] {
      border: 1px #666 dashed;
      background-color: #d7ffd9;
    }
    [data-contenteditable=false],
    body.mce-content-body[contenteditable=true] :not(.hero-template-placeholder)[contenteditable=false],
    [contenteditable=true] :not(.hero-template-placeholder)[contenteditable=false] {
      border: 1px #666 dashed;
      background-color: #ffd2c9;
    }
    body.mce-content-body:not([contenteditable=true]) [contenteditable=true] :not(.hero-template-placeholder)[contenteditable=false],
    body.mce-content-body:not([contenteditable=true]) span.hero-template-placeholder {
      background-color: #ddd;
    }
    body.mce-content-body[contenteditable=true] :not(.hero-template-placeholder)[contenteditable=false] [contenteditable=true] {
      background-color: #fff;
    }
  `;

// postTemplateTinyMCEConfig provides a TinyMCE config for authoring Post
// Templates, by adding custom toolbar buttons and formats.
export function postTemplateTinyMCEConfig(
  onSetup: (editor: Editor) => void,
  openPlaceholderDialog: () => void,
): TinyMCEConfig {
  // addPostTemplateUI adds the buttons and formats to the editor. The setup
  // callback calls it, because TinyMCE loads lazily (see tinymce-loader) and
  // the global tinymce does not exist when the dialog makes this config.
  const addPostTemplateUI = (editor: Editor) => {
    editor.ui.registry.addButton('heroPostTemplatePlaceholder', {
      icon: 'addtag',
      tooltip: 'Insert/Edit Placeholder',
      onAction: () => {
        // TS-NARROWED: no caller omits openPlaceholderDialog.
        if (openPlaceholderDialog) {
          openPlaceholderDialog();
        }
      },
    });
    editor.ui.registry.addToggleButton('heroPostTemplateEditableArea', {
      icon: 'edit-block',
      tooltip: 'Editable Area',
      onAction: () => {
        editor.formatter.toggle('hero-editable');
      },
      onSetup: (api) => {
        api.setActive(editor.formatter.match('hero-editable'));
        const changed = editor.formatter.formatChanged(
          'hero-editable',
          (state) => api.setActive(state),
        );
        return () => changed.unbind();
      },
    });
    editor.ui.registry.addToggleButton('heroPostTemplateStaticArea', {
      icon: 'lock',
      tooltip: 'Non-editable Area',
      onAction: () => {
        editor.formatter.toggle('hero-noneditable');
      },
      onSetup: (api) => {
        api.setActive(editor.formatter.match('hero-noneditable'));
        const changed = editor.formatter.formatChanged(
          'hero-noneditable',
          (state) => api.setActive(state),
        );
        return () => changed.unbind();
      },
    });
    // format_empty_lines is an editor option, not a format option. The format
    // types do not accept it, so the specs use a cast.
    // TS-BUG: TinyMCE ignores format_empty_lines in a format spec. Move it to
    // the editor config if empty lines must get the format.
    editor.on('init', () => {
      editor.formatter.register('hero-editable', {
        inline: 'span',
        selector:
          'p,h1,h2,h3,h4,h5,h6,pre,div,ul,ol,li,table,thead,tbody,tr,th,td',
        attributes: { 'data-contenteditable': 'true' },
        format_empty_lines: true,
        merge_siblings: false,
        exact: true,
      } as Formats.Format);
      editor.formatter.register('hero-noneditable', {
        inline: 'span',
        selector:
          'p,h1,h2,h3,h4,h5,h6,pre,div,ul,ol,li,table,thead,tbody,tr,th,td',
        attributes: { 'data-contenteditable': 'false' },
        format_empty_lines: true,
        merge_siblings: false,
        exact: true,
      } as Formats.Format);
    });
  };

  const config = defaultTinyMCEConfig();
  config.setup = (editor) => {
    addPostTemplateUI(editor);
    // TS-NARROWED: no caller omits onSetup.
    if (onSetup) {
      onSetup(editor);
    }
  };
  config.toolbar +=
    ' | heroPostTemplatePlaceholder heroPostTemplateEditableArea heroPostTemplateStaticArea';
  config.toolbar_mode = 'wrap';
  config.content_css = ['default', '/assets/icons/linced-material.css'];
  config.content_style = TemplatedPostEditorStyles;
  return config;
}

// templatedPostTinyMCEConfig provides a TinyMCE config for posts based on Post
// Templates, locking it down if it's a fixed template (editableRoot=false).
export function templatedPostTinyMCEConfig(
  editableRoot: boolean,
): TinyMCEConfig {
  const config = defaultTinyMCEConfig();
  if (!editableRoot) {
    config.editable_root = false;
    config.plugins = config.plugins.replace(/\bcode\b/, '').trim();
    // TS-NARROWED: defaultTinyMCEConfig always sets menu.tools.items.
    if (config.menu && config.menu.tools && config.menu.tools.items) {
      config.menu.tools.items = config.menu.tools.items
        .replace(/\bcode\b/, '')
        .trim();
    }
  }
  config.content_css = ['default', '/assets/icons/linced-material.css'];
  config.content_style = TemplatedPostEditorStyles;
  return config;
}

// removeAutosavedDrafts removes a post draft's keys generated by TinyMCE's Autosave plugin
export function removeAutosavedDrafts(path: string, editorId: string) {
  const autosaveKey = `tinymce-autosave-${path}-${editorId}`;
  localStorage.removeItem(`${autosaveKey}-draft`);
  localStorage.removeItem(`${autosaveKey}-time`);
}

// autosaveCreateEditorId generates an editorId to be assigned to the tiny-editor component
// to target autosaves
export function autosaveCreateEditorId(
  currentPersonId: string | null,
  selectedPersonId: string | null,
  additionalPrefixesString?: string | null,
) {
  // Pattern:
  // 'person's ID creating the autosaved draft - selected person's ID the post is for - any additionals'
  let editorId = '';

  if (isEmpty(currentPersonId)) {
    throw new Error('Current person ID is required');
  }
  // TS-NARROWED: no caller sends an ID that is not a string.
  if (typeof currentPersonId !== 'string') {
    throw new Error('Current person ID needs to be a string');
  }

  editorId += currentPersonId;

  if (!isEmpty(selectedPersonId)) {
    editorId += '-' + selectedPersonId;
  }

  if (!isEmpty(additionalPrefixesString)) {
    editorId += '-' + additionalPrefixesString;
  }

  return editorId;
}
// hasAutosaveDraft checks if localStorage contains a draft for the given path and
// editorId and if the draft's content is not empty
export function hasAutosaveDraft(path: string, editorId: string) {
  const autosaveKey = `tinymce-autosave-${path}-${editorId}-draft`;
  const draft = localStorage.getItem(autosaveKey);
  // prevent at least one whitespace from being considered a draft
  return !isEmpty(draft) && draft !== '<p>&nbsp;</p>';
}
// disableRestoreButtons manually disables any restore buttons it finds in the toolbar
export function disableRestoreButtons(mode: boolean) {
  const toolbar = document.querySelector('.tox-editor-header');
  if (toolbar) {
    const buttons = toolbar.querySelectorAll<HTMLButtonElement>(
      '[title="Restore last draft"]',
    );
    buttons.forEach((button) => (button.disabled = mode));
  }
}

export function manageAutosaveDraftsButton(editorId: string) {
  // TinyMCE's autosave plugin did not account for having multiple drafts
  // on multiple pages, so we have to manually trigger buttons if we locate
  // a specific draft
  const path = window.location.pathname;
  if (!hasAutosaveDraft(path, editorId)) {
    disableRestoreButtons(true);
  } else {
    disableRestoreButtons(false);
  }
}
